import { useRef, type ReactNode } from "react";

import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useMediaQuery } from "@/hooks/useMediaQuery";

import { focusOpenedFrom, type OpenedByRefs } from "../hooks/useOpenedBy";

/** A phone's width: below Tailwind's sm. */
export const PHONE_QUERY = "(max-width: 639px)";

/** Phones get full-width bottom sheets where a desktop gets a small popover (ui-rules 9). */
export const useIsPhone = () => useMediaQuery(PHONE_QUERY);

/** Scrolls `list` the least that shows all of `row`, as scrollIntoView's "nearest" would, but only
 * `list`: the page behind a sheet still sliding in must not move. */
function scrollRowIntoList(list: HTMLElement, row: HTMLElement) {
  let top = 0;
  let el: Element | null = row;
  while (el instanceof HTMLElement && el !== list) {
    top += el.offsetTop;
    el = el.offsetParent;
  }
  if (top < list.scrollTop) list.scrollTop = top;
  else if (top + row.offsetHeight > list.scrollTop + list.clientHeight) {
    list.scrollTop = top + row.offsetHeight - list.clientHeight;
  }
}

/**
 * A full-width bottom sheet with a title for screen readers only; rows go in `children`.
 * `openedBy` and `openedFrom` are the trigger's useOpenedBy: how the sheet was opened, and from where.
 */
export function PhoneSheet({
  open,
  onOpenChange,
  title,
  openedBy,
  openedFrom,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
} & OpenedByRefs) {
  const content = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        ref={content}
        side="bottom"
        aria-describedby={undefined}
        // A keyboard open focuses the current row (a pressed one), else the corner's Close. A tap's
        // open focuses the sheet itself, where no ring shows, and only brings the row into view.
        onOpenAutoFocus={(event) => {
          const current = content.current?.querySelector<HTMLElement>('[aria-pressed="true"]');
          if (openedBy.current === "pointer") {
            event.preventDefault();
            content.current?.focus({ preventScroll: true });
            if (current && list.current) scrollRowIntoList(list.current, current);
          } else if (current) {
            event.preventDefault();
            current.focus();
          }
        }}
        onCloseAutoFocus={(event) => focusOpenedFrom(event, { openedBy, openedFrom })}
        className="flex max-h-[90dvh] flex-col rounded-t-xl px-2 pb-[max(1rem,env(safe-area-inset-bottom))] pt-10 focus:outline-none motion-reduce:animate-none [&>button:first-child]:right-2 [&>button:first-child]:top-2 [&>button:first-child]:p-3"
      >
        <SheetTitle className="sr-only">{title}</SheetTitle>
        {/* Taller than the screen scrolls inside, so the sheet's top and its Close stay in view. */}
        <div ref={list} className="relative min-h-0 overflow-y-auto">
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}
